import { useEffect, useState } from "react";
import { Eye, Crosshair, Leaf, ShieldCheck, Drop, TreeEvergreen, UsersThree, Recycle, Globe, HandHeart } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_brief-builder-33/artifacts/lyhf4kb1_Screenshot%202026-06-09%20at%206.25.03%E2%80%AFPM.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const issueIcons = [Leaf, Drop, ShieldCheck, Globe, Drop, TreeEvergreen, Recycle, Globe, ShieldCheck, UsersThree];

export default function AboutPage() {
  const { t } = useLanguage();
  const issuesList = t("issues_list") || [];
  const missionItems = t("mission_items") || [];
  const [contentSections, setContentSections] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/content-sections`)
      .then((r) => r.json())
      .then((data) => setContentSections(Array.isArray(data) ? data : []))
      .catch(() => setContentSections([]));
  }, []);

  return (
    <div data-testid="about-page">
      {/* Page Header */}
      <section
        className="py-20 md:py-28 border-b"
        style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p
            className="text-xs uppercase tracking-[0.2em] font-medium mb-4"
            style={{ color: '#B85C38' }}
          >
            {t("about_tagline")}
          </p>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium"
            style={{ color: '#1C211F' }}
          >
            {t("about_heading")}
          </h1>
        </div>
      </section>

      {/* About Content */}
      <section
        data-testid="about-content-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
            {/* Text */}
            <div className="md:col-span-7 space-y-6">
              <p className="text-base leading-relaxed" style={{ color: '#4A5450' }}>
                {t("about_p1")}
              </p>
              <p className="text-base leading-relaxed" style={{ color: '#4A5450' }}>
                {t("about_p2")}
              </p>
              <p className="text-base leading-relaxed" style={{ color: '#4A5450' }}>
                {t("about_p3")}
              </p>
            </div>

            {/* Image */}
            <div className="md:col-span-5">
              <div className="rounded-lg overflow-hidden">
                <img
                  src={ABOUT_IMG}
                  alt="Bengaluru lake landscape"
                  className="w-full h-64 md:h-80 object-cover"
                  data-testid="about-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Issues We Work On */}
      <section
        data-testid="issues-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#E8EDE9' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <p
              className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
              style={{ color: '#B85C38' }}
            >
              {t("issues_tagline")}
            </p>
            <h2
              className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
              style={{ color: '#1C211F' }}
            >
              {t("issues_heading")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {issuesList.map((label, i) => {
              const Icon = issueIcons[i] || Leaf;
              return (
                <div
                  key={i}
                  data-testid={`issue-item-${i}`}
                  className="flex items-center gap-4 p-5 rounded-lg border"
                  style={{
                    backgroundColor: '#F7F5F0',
                    borderColor: 'rgba(26, 92, 55, 0.1)',
                  }}
                >
                  <Icon size={22} weight="duotone" style={{ color: '#1A5C37', flexShrink: 0 }} />
                  <span className="text-sm font-medium" style={{ color: '#1C211F' }}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section
        data-testid="vision-mission-section"
        className="py-24 md:py-32"
        style={{ backgroundColor: '#F7F5F0' }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            {/* Vision */}
            <div
              className="p-8 md:p-10 rounded-lg border"
              style={{ backgroundColor: '#1A5C37', borderColor: 'transparent' }}
              data-testid="vision-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <Eye size={28} weight="duotone" style={{ color: '#F7F5F0' }} />
                <h3 className="font-heading text-xl sm:text-2xl font-medium text-white">
                  {t("vision_label")}
                </h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(247, 245, 240, 0.85)' }}>
                {t("vision_text")}
              </p>
            </div>

            {/* Mission */}
            <div
              className="p-8 md:p-10 rounded-lg border"
              style={{ backgroundColor: '#F7F5F0', borderColor: 'rgba(26, 92, 55, 0.1)' }}
              data-testid="mission-card"
            >
              <div className="flex items-center gap-3 mb-6">
                <Crosshair size={28} weight="duotone" style={{ color: '#1A5C37' }} />
                <h3
                  className="font-heading text-xl sm:text-2xl font-medium"
                  style={{ color: '#1C211F' }}
                >
                  {t("mission_label")}
                </h3>
              </div>
              <ul className="space-y-3">
                {missionItems.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm leading-relaxed"
                    style={{ color: '#4A5450' }}
                  >
                    <HandHeart size={16} weight="fill" style={{ color: '#B85C38', marginTop: 3, flexShrink: 0 }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {contentSections.map((section, sectionIndex) => (
        <section
          key={section.id}
          data-testid={`about-content-section-${sectionIndex}`}
          className="py-20 md:py-28 border-t"
          style={{
            backgroundColor: sectionIndex % 2 === 0 ? '#E8EDE9' : '#F7F5F0',
            borderColor: 'rgba(26, 92, 55, 0.1)',
          }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-10">
              <p
                className="text-xs uppercase tracking-[0.2em] font-medium mb-3"
                style={{ color: '#B85C38' }}
              >
                KYBIA
              </p>
              <h2
                className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium"
                style={{ color: '#1C211F' }}
              >
                {section.section_title}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(section.images || []).map((image, imageIndex) => (
                <div
                  key={`${image.url}-${imageIndex}`}
                  className="rounded-lg overflow-hidden border"
                  style={{ borderColor: 'rgba(26, 92, 55, 0.1)', backgroundColor: '#FFFFFF' }}
                >
                  <img
                    src={`${BACKEND_URL}${image.url}`}
                    alt={image.name || section.section_title}
                    className="w-full aspect-[4/3] object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
